import type { MetricDataType } from '@/entities/optimization-statistic'
import type {
	FilterRuleModel,
	OptimizationIDModel,
	TargetProtectDistributionModel,
} from '@/shared/generated'
import type { TFunction } from 'i18next'

import { useMemo } from 'react'
import {
	createColumnHelper,
	getCoreRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table'
import { values } from 'lodash/fp'
import { AlertTriangleIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useMetricsQuery } from '@/entities/bi'
import { useSelectedCurrency } from '@/entities/currency'
import { useStatisticsTargetProtectDistributionQuery } from '@/entities/optimization-statistic'

import { DataTable } from '@/shared/components/tables'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { MetricMeasure } from '@/shared/generated'
import { formatMoney, formatNumber, NumberFormats } from '@/shared/tools/locale'

import { getUnprefixedMetricKey, roundIfFloat } from '../lib/helpers'

export const BusinessMetricsTable = ({
	queryParams,
	isLoading: isDataLoading,
}: {
	queryParams: {
		optimizations: OptimizationIDModel[]
		filters: FilterRuleModel[]
	} | null
	isLoading: boolean
}) => {
	const { t } = useTranslation()

	const currency = useSelectedCurrency()
	const metricsQuery = useMetricsQuery()
	const targetProtectDistributionStatisticQuery =
		useStatisticsTargetProtectDistributionQuery(
			{ ...queryParams!, currency },
			{
				enabled: !!metricsQuery.data && !!queryParams && !isDataLoading,
				select: (data) =>
					values(
						data?.reduce(
							(
								acc: Record<string, any>,
								{ metric, type, value }: TargetProtectDistributionModel
							) => {
								if (!acc[metric]) {
									acc[metric] = {
										name: metric,
									}
								}
								const metricSchema = metricsQuery.data?.find(
									({ name }) => name === metric || name === metric.split('.')[1]
								)
								acc[metric][type] = value
								acc[metric].dataType = metricSchema?.measure

								return acc
							},
							{}
						)
					),
			}
		)
	const isLoading =
		targetProtectDistributionStatisticQuery.isLoading || isDataLoading
	const isError = targetProtectDistributionStatisticQuery.isError
	const columns = useMemo(() => getColumns(t, currency), [currency])
	const table = useReactTable<DistributionStatistic>({
		columns,
		defaultColumn: {
			minSize: 28,
			size: Number.MAX_SAFE_INTEGER,
			maxSize: Number.MAX_SAFE_INTEGER,
		},
		data: targetProtectDistributionStatisticQuery.data || [],
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
	})
	if (isLoading) {
		return <LoadingPlaceholder />
	}
	if (isError) {
		return (
			<Alert variant='destructive'>
				<AlertTriangleIcon />
				<AlertDescription>
					{targetProtectDistributionStatisticQuery.error.message ||
						t('fatal_error_title')}
				</AlertDescription>
			</Alert>
		)
	}

	return (
		<div className='overflow-hidden rounded-lg border'>
			<DataTable compact table={table} getRowClassName={() => 'h-8'} />
		</div>
	)
}
function getColumns(t: TFunction, currency?: string) {
	const columnHelper = createColumnHelper<DistributionStatistic>()

	return [
		columnHelper.accessor('name', {
			header: () =>
				t('opt.summary.business_metrics', { defaultValue: 'Business metrics' }),
			cell: (info) => {
				const value = info.getValue()
				const key = getUnprefixedMetricKey(value).split('.')[0]

				return <div className='text-sm'>{t(key, { defaultValue: value })}</div>
			},
		}),
		columnHelper.accessor('target', {
			size: 70,
			header: () => t('og_metric_to_grow', { defaultValue: 'Grow' }),
			cell: (info) => {
				const metricType = info.row.original.dataType

				return (
					<div className='text-sm'>
						{formatValueByMetricType({
							metricType,
							value: info.getValue(),
							currency,
						})}
					</div>
				)
			},
		}),
		columnHelper.accessor('protect', {
			size: 70,
			header: () => t('og_metric_to_maintain', { defaultValue: 'Maintain' }),
			cell: (info) => {
				const metricType = info.row.original.dataType

				return (
					<div className='text-sm'>
						{formatValueByMetricType({
							metricType,
							value: info.getValue(),
							currency,
						})}
					</div>
				)
			},
		}),
		columnHelper.accessor('other', {
			size: 70,
			header: () => t('og_metric_to_other', { defaultValue: 'Other' }),
			cell: (info) => {
				const metricType = info.row.original.dataType

				return (
					<div className='text-sm'>
						{formatValueByMetricType({
							metricType,
							value: info.getValue(),
							currency,
						})}
					</div>
				)
			},
		}),
	]
}
const formatValueByMetricType = ({
	metricType,
	value,
	currency,
}: {
	metricType: string
	value: number
	currency?: string
}) => {
	switch (metricType) {
		case MetricMeasure.Percent:
			return formatNumber(value * 100, NumberFormats.Percent)
		case MetricMeasure.Money:
			return formatMoney(value, currency, NumberFormats.Compact)
		case MetricMeasure.Unit:
			return formatNumber(roundIfFloat(value))
		default:
			return formatNumber(value, NumberFormats.Compact)
	}
}
type DistributionStatistic = {
	name: string
	protect: number
	target: number
	other: number
	dataType: MetricDataType
}
function LoadingPlaceholder() {
	return (
		<div className='border-base flex max-h-[400px] w-full flex-col overflow-hidden rounded-sm border border-solid p-3'>
			<div className='flex flex-col items-center space-y-2'>
				<div className='flex w-full items-center justify-between'>
					<Skeleton className='h-5 w-20' />
					<Skeleton className='h-5 w-10' />
				</div>
				<div className='flex w-full items-center justify-between'>
					<Skeleton className='h-5 w-24' />
					<Skeleton className='h-5 w-12' />
				</div>
				<div className='flex w-full items-center justify-between'>
					<Skeleton className='h-5 w-28' />
					<Skeleton className='h-5 w-12' />
				</div>
			</div>
		</div>
	)
}
